import React from "react";
import { Box, Typography, Button, Container, Stack } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const HeroSection = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: "auto", md: "calc(100vh - 80px)" }, // Dynamic height to fit the screen
        minHeight: { xs: "600px", md: "700px" },
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center", // Vertically center the entire content block
        justifyContent: "flex-start",
        backgroundColor: "#1C1917", // Fallback color
      }}
    >
      {/* Background Image with Slow Zoom Animation */}
      <motion.div
        initial={{ scale: 1 }}
        animate={{ scale: 1.1 }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "linear",
        }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url('https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=2000&auto=format&fit=crop')`, // Using a high-quality placeholder, user can replace with generated asset
          backgroundSize: "cover",
          backgroundPosition: "center",
          zIndex: 0,
        }}
      />

      {/* Elegant Dark Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(to right, rgba(28, 25, 23, 0.8) 0%, rgba(28, 25, 23, 0.4) 50%, rgba(28, 25, 23, 0.2) 100%)",
          zIndex: 1,
        }}
      />

      {/* Content Container */}
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1280px",
          position: "relative",
          zIndex: 2,
          px: { xs: 3, md: 6 },
          height: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "768px" },
            height: { xs: "auto", md: "528px" },
            maxWidth: "768px",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start", // Force left alignment for all children
            justifyContent: "center",
            gap: "24px",
            opacity: 1,
            py: { xs: 4, md: 0 },
          }}
        >
          {/* Tagline & Header Group */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* Tagline Content */}
            <Box
              sx={{
                width: "282px",
                height: "19px",
                display: "flex",
                alignItems: "center",
                opacity: 1,
              }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "12px",
                    lineHeight: "19px",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    color: "#FFFFFF",
                    backgroundColor: "#FF5722",
                    px: 1.5,
                    py: 0.2,
                    borderRadius: "2px",
                    display: "inline-block",
                  }}
                >
                  Curated Living Environments
                </Typography>
              </motion.div>
            </Box>

            {/* Header Content */}
            <Box
              sx={{
                width: { xs: "100%", md: "768px" },
                height: { xs: "auto", md: "288px" },
                display: "flex",
                flexDirection: "column",
                textAlign: "left",
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 800,
                    fontSize: { xs: "48px", sm: "72px", md: "96px" },
                    lineHeight: { xs: "1.1", md: "96px" },
                    color: "#FFFFFF",
                    letterSpacing: { xs: "-1.5px", md: "-4.8px" },
                    textTransform: "none",
                    textAlign: "left",
                  }}
                >
                  Bespoke <br />
                  <Box component="span" sx={{ color: "#FF5722" }}>
                    Interior <br />
                    Excellence
                  </Box>
                </Typography>
              </motion.div>
            </Box>
          </Box>

          <Box
            sx={{
              width: { xs: "100%", md: "576px" },
              height: { xs: "auto", md: "120px" },
              maxWidth: "576px",
              paddingTop: "8px",
              textAlign: "left",
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: { xs: "16px", md: "20px" },
                  color: "#E7E5E4",
                  lineHeight: { xs: "1.5", md: "28px" },
                  fontWeight: 300,
                  letterSpacing: "0px",
                  textAlign: "left",
                }}
              >
                Merging architectural precision with artisanal craft to curate
                spaces that transcend utility. We define the intersection of
                luxury and functionality through meticulous spatial
                storytelling.
              </Typography>
            </motion.div>
          </Box>

          {/* Button Container */}
          <Box
            sx={{
              width: { xs: "100%", md: "768px" },
              height: { xs: "auto", md: "72px" },
              display: "flex",
              justifyContent: "flex-start",
              gap: "24px",
              paddingTop: "16px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              style={{ width: "auto" }} // Don't force full width container here
            >
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => {
                  const element = document.getElementById("begin-transformation");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "center" });
                  }
                }}
                sx={{
                  width: { xs: "100%", sm: "260px" },
                  height: "56px",
                  whiteSpace: "nowrap",
                  background:
                    "linear-gradient(90deg, #B02F00 0%, #FF5722 100%)",
                  borderRadius: "6px",
                  padding: "16px 32px",
                  display: "flex",
                  gap: "12px",
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #FF5722 0%, #B02F00 100%)",
                  },
                }}
              >
                Start Consultation
              </Button>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Decorative Element - Vertical Line */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          right: "10%",
          width: "1px",
          height: "200px",
          backgroundColor: "rgba(255, 87, 34, 0.5)",
          zIndex: 2,
          display: { xs: "none", md: "block" },
        }}
      />
    </Box>
  );
};

export default HeroSection;
